import { observer } from "mobx-react-lite";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import styled from "@emotion/styled";
import {
  MDSPanelHeader,
  MDSPanelHeaderSlotName,
  MDSText,
  MDSTextVariant,
  MDSIconButton,
  MDSPanelHeaderButtonGroup,
  MDSIconButtonSize,
  MDSIcon,
  MDSIconKind,
  MDSFadeHorizontalScroll,
  MDSProgressBar,
} from "@/mds";
import { ContactsFacetFilter } from "@/components/filters/contacts-facet-filter";
import { CollectionsFacetFilter } from "@/components/filters/collections-facet-filter";
import { MediaKindsFacetFilter } from "@/components/filters/media-kinds-facet-filter";
import { DateFacetFilter } from "@/components/filters/date-facet-filter";
import { SearchPageFilter } from "@/modules/filters/search/SearchPageFilter";
import { noop } from "lodash-es";
import { SortByKind } from "@/modules/lenses/types";
import { ClearFiltersModal, useClearFiltersModal } from "@/components/filters/clear-filters-modal";
import { SortByDropdownButton } from "@/components/filters/sort-by-dropdown-button";

export const SearchPageHeader = observer(function SearchPageHeader() {
  const page = useTabPageStore("searchPage");
  const queryString = page.filters.params.queryString;

  const {
    filterButtonRef,
    showFilters,
    showFiltersModal,
    setShowFiltersModal,
    handleToggleFilters,
    handleClearFilters,
  } = useClearFiltersModal({
    clearAllFilters: page.filters.clearAllFilters,
    activeFiltersCount: page.filters.activeFiltersCount,
    initialShowFilters: true,
  });

  return (
    <>
      <Wrapper>
        <MDSPanelHeader>
          <StyledHeading slot={MDSPanelHeaderSlotName.Heading}>
            <MDSIcon kind={MDSIconKind.Search} size={18} />
            <MDSText variant={MDSTextVariant.HeaderXl} numberOfLines={1}>
              {queryString ? queryString : "Notes"}
            </MDSText>
          </StyledHeading>
          <MDSPanelHeaderButtonGroup slot={MDSPanelHeaderSlotName.Actions}>
            <MDSIconButton
              ref={filterButtonRef}
              size={MDSIconButtonSize.Medium}
              iconKind={MDSIconKind.Filter}
              onClick={handleToggleFilters}
              isToggled={showFilters}
            />
          </MDSPanelHeaderButtonGroup>
        </MDSPanelHeader>
        {page.isLoading && <MDSProgressBar isIndeterminate />}
        {showFilters && (
          <WrapperBottom>
            <FiltersContainer>
              <SortByDropdownButton
                sortOptions={page.filters.sortOptions}
                defaultSortOption={SortByKind.Relevance}
                selectedSortOption={page.filters.sortBy}
                onSelectSortOption={page.filters.setSortBy}
                alwaysToggled
              />
              {renderSortedFilters(page.filters)}
            </FiltersContainer>
          </WrapperBottom>
        )}
      </Wrapper>
      <ClearFiltersModal
        triggerRef={filterButtonRef}
        isOpen={showFiltersModal}
        count={page.filters.activeFiltersCount}
        onCancel={() => setShowFiltersModal(false)}
        onConfirm={handleClearFilters}
      />
    </>
  );
});

const renderSortedFilters = (pageFilters: SearchPageFilter) => {
  const filters = [
    {
      isActive: pageFilters.collectionsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 0,
      component: (
        <CollectionsFacetFilter
          key="collections"
          searchText={pageFilters.collectionsSearchQuery}
          items={pageFilters.collectionsFacetFilterItems}
          onSearchTextChange={pageFilters.setCollectionSearchQuery}
          onSearchTextClear={pageFilters.clearCollectionSearchQuery}
          onSelectItem={pageFilters.toggleCollectionItem}
          onSelectClear={pageFilters.clearCollections}
          buttonPrefix="In"
          buttonLabel="Collections"
          selectedModeKey="in-any-of"
          onSelectMode={noop}
          noCollectionIsSelected={false}
          onNoCollectionIsSelected={noop}
          selectedItems={pageFilters.selectedCollectionTitles}
          supportModeSelection={false}
        />
      ),
    },
    {
      isActive: !!pageFilters.mediaKindsFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 1,
      component: (
        <MediaKindsFacetFilter
          key="media-kinds"
          items={pageFilters.mediaKindsFacetFilterItems}
          onSelectItem={pageFilters.toggleMediaKindItem}
          onSelectClear={pageFilters.clearMediaKinds}
          selectedItems={pageFilters.selectedMediaKinds}
        />
      ),
    },
    {
      isActive: !!pageFilters.dateParams,
      defaultOrder: 2,
      component: (
        <DateFacetFilter
          key="date"
          items={pageFilters.dateFilterItems}
          selectedRange={pageFilters.dateSelectedRange}
          onClear={() => pageFilters.clearDateFilter()}
          dateKindLabel={pageFilters.sortBy === SortByKind.LastCreated ? "Created" : "Edited"}
        />
      ),
    },
    {
      isActive: pageFilters.modifiedByFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 3,
      component: (
        <ContactsFacetFilter
          key="modifiedBy"
          items={pageFilters.modifiedByFacetFilterItems}
          searchText={pageFilters.modifiedBySearchQuery}
          onSearchTextChange={pageFilters.setModifiedBySearchQuery}
          onSearchTextClear={pageFilters.clearModifiedBySearchQuery}
          onSelectItem={pageFilters.toggleModifiedByItem}
          onSelectClear={pageFilters.clearModifiedBy}
          buttonPrefix="Edited by"
        />
      ),
    },
    {
      isActive: pageFilters.createdByFacetFilterItems.some(item => item.isSelected),
      defaultOrder: 4,
      component: (
        <ContactsFacetFilter
          key="createdBy"
          items={pageFilters.createdByFacetFilterItems}
          searchText={pageFilters.createdBySearchQuery}
          onSearchTextChange={pageFilters.setCreatedBySearchQuery}
          onSearchTextClear={pageFilters.clearCreatedBySearchQuery}
          onSelectItem={pageFilters.toggleCreatedByItem}
          onSelectClear={pageFilters.clearCreatedBy}
          buttonPrefix="Created by"
        />
      ),
    },
  ];

  return filters
    .sort((a, b) => {
      const activeSort = Number(b.isActive) - Number(a.isActive);
      if (activeSort !== 0) {
        return activeSort;
      }

      return a.defaultOrder - b.defaultOrder;
    })
    .map(filter => filter.component);
};

const Wrapper = styled.div({});

const StyledHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const WrapperBottom = styled.div(({ theme }) => ({
  padding: theme.spacing["4"],
}));

const FiltersContainer = styled(MDSFadeHorizontalScroll)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing["2"],
}));
